import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../redux/store';
import { IChat } from '../../interfaces/IChatUser';
import { updateMessageReactions } from '../../redux/chatSlice';
import { Box, Paper, IconButton, TextField, Typography, Tooltip, useMediaQuery, useTheme, Avatar, Badge } from '@mui/material';
import { keyframes } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { AnimatedReactionIcon } from '../reactions';
import { PostReactionPicker } from '../reactions/PostReactionPicker';
import { toast } from '../snackbar';
import { ConversationService } from '../../services/conversationService';
import { useApiClient } from '../../ApiClientProvider';
import type { CommunityReactionType } from '../../interfaces/ICommunityHub';

const typingBounce = keyframes`
    0%, 60%, 100% { opacity: 0.35; transform: translateY(0); }
    30% { opacity: 1; transform: translateY(-3px); }
`;

interface TypingIndicatorProps {
    name?: string;
}

const TypingIndicator: React.FC<TypingIndicatorProps> = ({ name }) => {
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.75,
                    py: 1.25,
                    borderRadius: '18px 18px 18px 4px',
                    bgcolor: '#ffffff',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    maxWidth: '75%',
                }}
                aria-label={name ? `${name} is typing` : 'Typing'}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {[0, 1, 2].map(i => (
                        <Box
                            key={i}
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: 'text.secondary',
                                ...(prefersReducedMotion
                                    ? { opacity: 0.5 }
                                    : { animation: `${typingBounce} 1.2s infinite ease-in-out`, animationDelay: `${i * 0.15}s` }),
                            }}
                        />
                    ))}
                </Box>
                {name && (
                    <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        {name} is typing
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

interface ChatWindowProps {
    chat: IChat;
    currentUserId: string;
    onClose: (chatId: string) => void;
    onMinimize: (chatId: string) => void;
    onSendMessage: (chatId: string, content: string) => void;
    onTyping?: (chatId: string) => void;
    onStopTyping?: (chatId: string) => void;
    onRetryMessage?: (chatId: string, clientId: string) => void;
    style?: React.CSSProperties;
}

// How often, while actively composing, we re-send a "typing" ping. A steady
// heartbeat (rather than only on each keystroke) keeps the recipient's indicator
// alive through natural mid-sentence pauses so it never flickers off.
const TYPING_HEARTBEAT_MS = 2000;

// If no keystroke happens for this long we treat the user as having stopped typing
// and send an explicit stop, even if the field still holds text.
const TYPING_IDLE_CAP_MS = 6000;

const ChatWindow: React.FC<ChatWindowProps> = ({
    chat,
    currentUserId,
    onClose,
    onMinimize,
    onSendMessage,
    onTyping,
    onStopTyping,
    onRetryMessage,
    style
}) => {
    const dispatch = useDispatch();
    const apiClient = useApiClient('applicationClient');
    const conversationService = React.useMemo(() => new ConversationService(apiClient), [apiClient]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Mobile: < 600px
    const [draft, setDraft] = useState('');
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [hasDragged, setHasDragged] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [savingReactions, setSavingReactions] = useState<Set<string>>(new Set());
    const pendingReactions = useRef(new Set<string>());
    const messageInputRef = useRef<HTMLInputElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const chatWindowRef = useRef<HTMLDivElement>(null);
    const typingHeartbeatRef = useRef<NodeJS.Timeout | null>(null);
    const lastKeystrokeAtRef = useRef<number>(0);

    const reduxChat = useSelector((state: RootState) =>
        state.chat.chats.find(c => c.id === chat.id)
    );
    const onlineStatus = useSelector((state: RootState) => state.chat.onlineStatus);
    const isReconnecting = useSelector((state: RootState) => state.chat.isReconnecting);

    const isTyping = reduxChat?.isTyping || false;
    const messages = reduxChat?.messages || chat.messages;

    console.log('💬 ChatWindow render - isTyping:', isTyping, 'chatId:', chat.id, 'reduxChat:', reduxChat);

    const otherParticipant = chat.participants.find(p => p.id !== currentUserId);
    const isOtherUserOnline = otherParticipant ? (onlineStatus[otherParticipant.id] ?? otherParticipant.isOnline ?? false) : false;

    useEffect(() => {
        if (messagesEndRef.current && !reduxChat?.isMinimized) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages.length, isTyping, reduxChat?.isMinimized]);

    // Auto-focus input when chat window opens or is un-minimized
    useEffect(() => {
        if (!reduxChat?.isMinimized && !isMobile && messageInputRef.current) {
            messageInputRef.current.focus();
        }
    }, [reduxChat?.isMinimized, isMobile]);

    useEffect(() => {
        if (!reduxChat?.isMinimized && (position.y !== 0 || position.x !== 0)) {
            const windowHeight = window.innerHeight;
            const windowWidth = window.innerWidth;
            const chatHeight = 420;
            const chatWidth = 360;

            let newX = position.x;
            let newY = position.y;
            let needsUpdate = false;

            if (position.y + chatHeight > windowHeight) {
                newY = Math.max(0, windowHeight - chatHeight - 20);
                needsUpdate = true;
            }

            if (position.x + chatWidth > windowWidth) {
                newX = Math.max(0, windowWidth - chatWidth - 20);
                needsUpdate = true;
            }

            if (needsUpdate) {
                setPosition({ x: newX, y: newY });
            }
        }
    }, [reduxChat?.isMinimized, position.x, position.y]);

    const stopTypingHeartbeat = useCallback(() => {
        if (typingHeartbeatRef.current) {
            clearInterval(typingHeartbeatRef.current);
            typingHeartbeatRef.current = null;
        }
        lastKeystrokeAtRef.current = 0;
        onStopTyping?.(chat.id);
    }, [chat.id, onStopTyping]);

    const handleSend = useCallback(() => {
        if (draft.trim()) {
            onSendMessage(chat.id, draft.trim());
            setDraft('');
            stopTypingHeartbeat();
        }
    }, [chat.id, draft, onSendMessage, stopTypingHeartbeat]);

    const handleInput = useCallback((value: string) => {
        setDraft(value);

        // Cleared the composer: tell the recipient typing has stopped.
        if (!value.trim()) {
            stopTypingHeartbeat();
            return;
        }

        lastKeystrokeAtRef.current = Date.now();

        // Start a steady heartbeat on the first keystroke of a burst. It keeps
        // pinging the recipient every TYPING_HEARTBEAT_MS — even through pauses —
        // until the user has been idle for TYPING_IDLE_CAP_MS or clears the field.
        if (!typingHeartbeatRef.current) {
            onTyping?.(chat.id);
            typingHeartbeatRef.current = setInterval(() => {
                if (Date.now() - lastKeystrokeAtRef.current >= TYPING_IDLE_CAP_MS) {
                    stopTypingHeartbeat();
                    return;
                }
                onTyping?.(chat.id);
            }, TYPING_HEARTBEAT_MS);
        }
    }, [chat.id, onTyping, stopTypingHeartbeat]);

    const handleInputFocus = useCallback(() => {
        // On mobile, scroll input into view when keyboard appears
        if (isMobile && messageInputRef.current) {
            setTimeout(() => {
                messageInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 300);
        }
    }, [isMobile]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (isMobile || !chatWindowRef.current) return;
        const rect = chatWindowRef.current.getBoundingClientRect();
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        });
    }, [isMobile]);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isDragging || !chatWindowRef.current) return;

        setHasDragged(true);

        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const rect = chatWindowRef.current.getBoundingClientRect();

        let newX = e.clientX - dragOffset.x;
        let newY = e.clientY - dragOffset.y;

        newX = Math.max(0, Math.min(newX, windowWidth - rect.width));
        newY = Math.max(0, Math.min(newY, windowHeight - rect.height));

        setPosition({ x: newX, y: newY });
    }, [isDragging, dragOffset]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
        setTimeout(() => setHasDragged(false), 100);
    }, []);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    useEffect(() => {
        const handleResize = () => {
            if (!chatWindowRef.current) return;
            const rect = chatWindowRef.current.getBoundingClientRect();
            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;

            if (rect.right > windowWidth || rect.bottom > windowHeight) {
                let newX = position.x;
                let newY = position.y;

                if (rect.right > windowWidth) {
                    newX = Math.max(0, windowWidth - rect.width);
                }
                if (rect.bottom > windowHeight) {
                    newY = Math.max(0, windowHeight - rect.height);
                }

                setPosition({ x: newX, y: newY });
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [position]);


    // Cleanup typing heartbeat and hover timeout on unmount
    useEffect(() => {
        return () => {
            if (typingHeartbeatRef.current) {
                clearInterval(typingHeartbeatRef.current);
            }
        };
    }, []);

    const handleReaction = useCallback(async (messageId: string, reactionType: CommunityReactionType, currentUserReaction?: string) => {
        if (pendingReactions.current.has(messageId)) return;
        pendingReactions.current.add(messageId);
        setSavingReactions(new Set(pendingReactions.current));
        try {
            // Check if user is removing their reaction
            if (currentUserReaction === reactionType) {
                // Remove reaction
                await conversationService.removeMessageReaction(messageId);
                // Optimistic update: set counts to 0 and remove user reaction
                const message = messages.find(m => m.id === messageId);
                if (message?.reactions) {
                    const newCounts = { ...message.reactions.counts };
                    newCounts[reactionType] = Math.max(0, (newCounts[reactionType] || 0) - 1);
                    if (newCounts[reactionType] === 0) {
                        delete newCounts[reactionType];
                    }
                    dispatch(updateMessageReactions({
                        chatId: chat.id,
                        messageId,
                        reactions: {
                            counts: newCounts,
                            userReaction: undefined,
                        },
                    }));
                }
            } else {
                // Add or update reaction
                const response = await conversationService.addOrUpdateMessageReaction(messageId, reactionType);
                // Optimistic update with server response
                dispatch(updateMessageReactions({
                    chatId: chat.id,
                    messageId,
                    reactions: {
                        counts: response.counts,
                        userReaction: reactionType,
                    },
                }));
            }
        } catch (error) {
            console.error('Failed to update reaction:', error);
            toast.error("We couldn't update your reaction. Please try again.");
        } finally {
            pendingReactions.current.delete(messageId);
            setSavingReactions(new Set(pendingReactions.current));
        }
    }, [chat.id, conversationService, dispatch, messages]);

    if (reduxChat?.isMinimized && !isMobile) {
        const handleOrbMouseDown = (e: React.MouseEvent) => {
            e.stopPropagation();
            if (!chatWindowRef.current) return;
            const rect = chatWindowRef.current.getBoundingClientRect();
            setIsDragging(true);
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
        };

        const handleOrbClick = (e: React.MouseEvent) => {
            if (!hasDragged) {
                onMinimize(chat.id);
            }
        };

        return (
            <Tooltip
                title={`${otherParticipant?.name || 'User'} - Click to open`}
                placement="top"
                componentsProps={{
                    tooltip: {
                        sx: {
                            bgcolor: 'rgba(0, 0, 0, 0.8)',
                            borderRadius: '8px',
                            px: 1.5,
                            py: 0.75,
                        }
                    }
                }}
            >
                <Box
                    ref={chatWindowRef}
                    onMouseDown={handleOrbMouseDown}
                    onClick={handleOrbClick}
                    tabIndex={0}
                    sx={{
                        position: 'fixed',
                        bottom: position.y === 0 ? 20 : 'auto',
                        right: position.x === 0 ? (style?.right ?? 120) : 'auto',
                        top: position.y !== 0 ? position.y : 'auto',
                        left: position.x !== 0 ? position.x : 'auto',
                        width: 64,
                        height: 64,
                        minWidth: 64,
                        minHeight: 64,
                        maxWidth: 64,
                        maxHeight: 64,
                        pointerEvents: 'auto',
                        cursor: isDragging ? 'grabbing' : 'pointer',
                        userSelect: 'none',
                        zIndex: 1500,
                        borderRadius: '50% !important',
                        overflow: 'hidden',
                        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                        animation: 'orbAppear 0.25s ease-out',
                        '@keyframes orbAppear': {
                            '0%': {
                                transform: 'scale(0.3)',
                                opacity: 0,
                            },
                            '100%': {
                                transform: 'scale(1)',
                                opacity: 1,
                            },
                        },
                        '&:hover': {
                            transform: 'scale(1.1)',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                        },
                        ...(!isMobile ? (style || {}) : {})
                    }}
                >
                    <Badge
                        badgeContent={reduxChat?.unreadCount || 0}
                        color="error"
                        overlap="circular"
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <Box sx={{ position: 'relative', width: 64, height: 64 }}>
                            <Avatar
                                src={otherParticipant?.avatar}
                                alt={otherParticipant?.name}
                                sx={{
                                    width: 64,
                                    height: 64,
                                    minWidth: 64,
                                    minHeight: 64,
                                    maxWidth: 64,
                                    maxHeight: 64,
                                    border: '3px solid',
                                    borderColor: 'primary.main',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    borderRadius: '50% !important',
                                    overflow: 'hidden',
                                    pointerEvents: 'none',
                                }}
                            >
                                {otherParticipant?.name?.charAt(0).toUpperCase() || 'U'}
                            </Avatar>
                            {isOtherUserOnline && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 8,
                                        right: 8,
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        bgcolor: '#4ade80',
                                        border: '2px solid white',
                                        boxShadow: '0 0 0 2px rgba(74,222,128,0.3)',
                                    }}
                                />
                            )}
                        </Box>
                    </Badge>
                </Box>
            </Tooltip>
        );
    }

    return (
        <Paper
            ref={chatWindowRef}
            elevation={isMobile ? 0 : 6}
            sx={{
                position: 'fixed',
                ...(isMobile ? {
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: '100vw',
                    height: '100%',
                    borderRadius: 0,
                    zIndex: 1500,
                    animation: 'slideInFromRight 0.25s ease-out',
                    '@keyframes slideInFromRight': {
                        '0%': {
                            transform: 'translateX(100%)',
                            opacity: 0,
                        },
                        '100%': {
                            transform: 'translateX(0)',
                            opacity: 1,
                        },
                    },
                } : {
                    bottom: position.y === 0 ? 20 : 'auto',
                    right: position.x === 0 ? (style?.right ?? 120) : 'auto',
                    top: position.y !== 0 ? position.y : 'auto',
                    left: position.x !== 0 ? position.x : 'auto',
                    width: 360,
                    height: 420,
                    maxWidth: '92vw',
                    borderRadius: 2,
                    animation: 'expandFromOrb 0.25s ease-out',
                    transformOrigin: 'bottom right',
                    '@keyframes expandFromOrb': {
                        '0%': {
                            transform: 'scale(0.85)',
                            opacity: 0,
                        },
                        '100%': {
                            transform: 'scale(1)',
                            opacity: 1,
                        },
                    },
                }),
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                bgcolor: 'background.paper',
                cursor: isDragging ? 'grabbing' : 'default',
                userSelect: isDragging ? 'none' : 'auto',
                ...(!isMobile ? (style || {}) : {})
            }}
        >
            <Box
                onMouseDown={handleMouseDown}
                sx={{
                    px: 2,
                    py: isMobile ? 2 : 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    cursor: isMobile ? 'default' : 'grab',
                    '&:active': {
                        cursor: isMobile ? 'default' : 'grabbing'
                    }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {isMobile && (
                        <IconButton
                            size="small"
                            onClick={() => onClose(chat.id)}
                            sx={{ color: 'inherit', mr: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                        >
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    )}
                    <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                            isOtherUserOnline ? (
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        bgcolor: '#4ade80',
                                        border: '2px solid',
                                        borderColor: 'primary.main',
                                        boxShadow: '0 0 0 2px rgba(74,222,128,0.3)',
                                    }}
                                />
                            ) : null
                        }
                    >
                        <Avatar
                            src={otherParticipant?.avatar}
                            alt={otherParticipant?.name}
                            sx={{
                                width: 32,
                                height: 32,
                                border: '2px solid rgba(255,255,255,0.3)',
                            }}
                        >
                            {otherParticipant?.name?.charAt(0).toUpperCase() || 'U'}
                        </Avatar>
                    </Badge>
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {otherParticipant?.name || 'Chat'}
                    </Typography>
                </Box>
                {!isMobile && (
                    <Box>
                        <Tooltip title={reduxChat?.isMinimized ? 'Restore' : 'Minimize'}>
                            <IconButton
                                size="small"
                                onClick={() => onMinimize(chat.id)}
                                sx={{ color: 'inherit', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                            >
                                <RemoveIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Close">
                            <IconButton
                                size="small"
                                onClick={() => onClose(chat.id)}
                                sx={{ color: 'inherit', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>

            {!reduxChat?.isMinimized && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    px: isMobile ? 1.5 : 2,
                    py: isMobile ? 1.5 : 2,
                    flex: 1,
                    overflowY: 'auto',
                    minHeight: 0,
                    bgcolor: '#f8f9fa',
                    ...(isMobile && {
                        paddingBottom: 'env(safe-area-inset-bottom)'
                    })
                }}>
                    {messages.map((msg) => {
                        const isMine = msg.senderId === currentUserId;
                        const isFailed = isMine && msg.status === 'failed';
                        return (
                            <Box
                                key={msg.clientId ?? msg.id}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: isMine ? 'flex-end' : 'flex-start',
                                    position: 'relative',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: '100%', justifyContent: isMine ? 'flex-end' : 'flex-start' }}>
                                <Box
                                    onClick={isFailed && msg.clientId ? () => onRetryMessage?.(chat.id, msg.clientId!) : undefined}
                                    sx={{
                                        px: 1.75,
                                        py: 1,
                                        borderRadius: isMine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                        bgcolor: isFailed ? 'error.main' : (isMine ? 'primary.main' : '#ffffff'),
                                        color: (isFailed || isMine) ? 'primary.contrastText' : 'text.primary',
                                        maxWidth: '75%',
                                        fontSize: '0.9rem',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                        wordBreak: 'break-word',
                                        cursor: isFailed ? 'pointer' : 'default',
                                        opacity: isMine && msg.status === 'sending' ? 0.7 : 1,
                                    }}
                                >
                                    {msg.content}
                                </Box>
                                
                                {!isMine && msg.status !== 'sending' && msg.status !== 'failed' && <PostReactionPicker message
                                    userReaction={msg.reactions?.userReaction as CommunityReactionType ?? null}
                                    disabled={savingReactions.has(msg.id)} onSelect={reaction => { void handleReaction(msg.id, reaction, msg.reactions?.userReaction); }} />}
                                </Box>
                                
                                {/* Show reaction icons for 1:1 chat — based on counts so cross-user reactions are visible */}
                                {msg.reactions && Object.entries(msg.reactions.counts ?? {}).some(([_, c]) => c > 0) && (
                                    <Box sx={{ mt: 0.5, px: 0.5, display: 'flex', gap: 0.25 }}>
                                        {Object.entries(msg.reactions.counts ?? {}).filter(([_, c]) => c > 0).map(([type]) => (
                                            <Box
                                                key={type}
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    bgcolor: 'background.paper',
                                                    border: msg.reactions?.userReaction === type ? '2px solid' : '1.5px solid',
                                                    borderColor: msg.reactions?.userReaction === type ? 'primary.main' : 'divider',
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <AnimatedReactionIcon
                                                    reaction={type as CommunityReactionType}
                                                    size={20}
                                                    animateOnHover={true}
                                                />
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                                
                                {isMine && msg.status && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.25, mr: 0.25 }}>
                                        {isFailed ? (
                                            <Typography variant="caption" color="error.main" sx={{ fontStyle: 'italic' }}>
                                                Failed — tap to retry
                                            </Typography>
                                        ) : msg.status === 'sending' ? (
                                            <AccessTimeIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                        ) : msg.status === 'read' ? (
                                            <DoneAllIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                        ) : (
                                            <DoneIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                        )}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                    {isTyping && (
                        <TypingIndicator name={otherParticipant?.name} />
                    )}
                    <div ref={messagesEndRef} />
                </Box>
            )}

            {!reduxChat?.isMinimized && (
                <Box sx={{
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    p: isMobile ? 1 : 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: '#ffffff',
                    ...(isMobile && {
                        position: 'sticky',
                        bottom: 0,
                        paddingBottom: 'calc(8px + env(safe-area-inset-bottom))'
                    })
                }}>
                    <TextField
                        inputRef={messageInputRef}
                        value={draft}
                        onChange={(e) => handleInput(e.target.value)}
                        onFocus={handleInputFocus}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSend(); } }}
                        placeholder="Type a message..."
                        size="small"
                        fullWidth
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '20px',
                                bgcolor: '#f0f2f5',
                                '& fieldset': { border: 'none' },
                                '&:hover fieldset': { border: 'none' },
                                '&.Mui-focused fieldset': { border: '1px solid', borderColor: 'primary.main' }
                            }
                        }}
                    />
                    <IconButton
                        color="primary"
                        onClick={handleSend}
                        disabled={!draft.trim()}
                        sx={{
                            bgcolor: draft.trim() ? 'primary.main' : 'transparent',
                            color: draft.trim() ? 'primary.contrastText' : 'action.disabled',
                            '&:hover': {
                                bgcolor: draft.trim() ? 'primary.dark' : 'transparent'
                            },
                            '&.Mui-disabled': {
                                bgcolor: 'transparent',
                                color: 'action.disabled'
                            }
                        }}
                    >
                        <SendIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}

            {isReconnecting && (
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1400,
                    borderRadius: 2
                }}>
                    <Box sx={{
                        bgcolor: 'background.paper',
                        px: 3,
                        py: 2,
                        borderRadius: 2,
                        boxShadow: 3,
                        textAlign: 'center'
                    }}>
                        <Typography variant="body2" color="text.secondary">
                            Reconnecting...
                        </Typography>
                    </Box>
                </Box>
            )}
        </Paper>
    );
};

export default ChatWindow;